import { useEffect, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import { usePronunciations } from "@/hooks/agent/usePronunciations";
import { usePronunciationVaultList } from "@/hooks/agent/usePronunciationVaults";
import { Agent, PronunciationPair } from "@/types/agent/agent";
import { mergePronunciations } from "@/lib/pronunciations";
import { PronunciationVault } from "@/types/agent/pronunciation-vault";
import { usePronunciationSuggester } from "@/components/common/PronunciationSuggester";
import { Badge } from "@/components/ui/badge";
import { AIChatService } from "@/services/api/AIChatService";

interface PronunciationModalProps {
  genie: Agent | null;
  isOpen: boolean;
  onClose: () => void;
}

const PronunciationModal = ({ genie, isOpen, onClose }: PronunciationModalProps) => {
  const [pronunciations, setPronunciations] = useState<PronunciationPair[]>([]);
  const [isSynced, setIsSynced] = useState(false);
  const [vaultSectionOpen, setVaultSectionOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate, isPending, error } = usePronunciations();
  const { data: vaults = [], isLoading: vaultsLoading } = usePronunciationVaultList();

  const updatePronunciation = (index: number, field: "word" | "pronunciation", value: string) => {
    setPronunciations((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setIsSynced(false);
  };

  const { SuggesterPanel, SuggestionBadge } = usePronunciationSuggester(pronunciations, (index, pronunciation) => {
    updatePronunciation(index, "pronunciation", pronunciation);
  });

  useEffect(() => {
    if (isOpen && genie) {
      const existingPronunciations = genie.settings?.pronunciations || [];
      setPronunciations(existingPronunciations as PronunciationPair[]);
      setVaultSectionOpen(false);

      const hasDictionaryId = genie.settings?.pronunciation_dictionary_id;
      const hasPronunciations = existingPronunciations.length > 0;
      setIsSynced(!!(hasDictionaryId && hasPronunciations));
    }
  }, [isOpen]);

  useEffect(() => {
    if (!error) return;
    console.error("Failed to update pronunciations:", error);
    setIsSynced(false);
  }, [error]);

  const handleSave = () => {
    if (!genie) return;
    mutate(
      { genie, pronunciations },
      {
        onSuccess: () => {
          setIsSynced(true);
          onClose();
        },
      },
    );
  };

  const addPronunciation = () => {
    setPronunciations((prev) => [{ word: "", pronunciation: "" }, ...prev]);
    setIsSynced(false);
  };

  const removePronunciation = (index: number) => {
    setPronunciations((prev) => prev.filter((_, i) => i !== index));
    setIsSynced(false);
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;

    setIsImporting(true);
    setImportError(null);

    try {
      const fileContent = await file.text();
      const pairs = await AIChatService.importPronunciationsFromFile({ fileContent });
      setPronunciations((current) => mergePronunciations(current, pairs));
      setIsSynced(false);
    } catch {
      setImportError("Failed to extract pronunciations from file. Please try again.");
    } finally {
      setIsImporting(false);
    }
  };

  const handleApplyVault = (vault: PronunciationVault) => {
    setPronunciations((current) => mergePronunciations(current, vault.pairs));
    setVaultSectionOpen(false);
    setIsSynced(false);
  };

  if (!genie) return null;

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Pronunciations - ${genie.name}`}
      description="Add custom pronunciations for words or names that your genie should pronounce differently."
      className="sm:!max-w-[50vw]"
    >
      <div className="flex flex-col gap-4 py-2">
        {pronunciations.length > 0 && (
          <div className={`flex items-center gap-2 text-sm ${isSynced ? "text-green-600" : "text-orange-600"}`}>
            <div className={`w-2 h-2 rounded-full ${isSynced ? "bg-green-500" : "bg-orange-500"}`} />
            <span>{isSynced ? "Synced with Genie" : "Not synced"}</span>
          </div>
        )}

        <div className="grid grid-cols-2 sm:flex gap-2">
          <button
            onClick={addPronunciation}
            className="col-span-2 sm:flex-1 px-4 py-3 rounded-md border text-sm transition-colors border-dashed"
            style={{ color: genie.brand || "#4b5563", borderColor: genie.brand || "#4b5563" }}
          >
            + Add Pronunciation
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json,.txt,.tsv,.md"
            className="hidden"
            onChange={handleFileImport}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-md border text-sm transition-colors border-dashed text-gray-500 border-gray-300 hover:border-primary/50 hover:text-primary disabled:opacity-50"
          >
            <Upload className="w-3.5 h-3.5" />
            {isImporting ? "Importing..." : "Import file"}
          </button>
          <button
            onClick={() => setVaultSectionOpen((v) => !v)}
            className={`px-4 py-3 rounded-md border text-sm transition-colors border-dashed ${
              vaultSectionOpen
                ? "text-primary border-primary/50 bg-primary/5"
                : "text-gray-500 border-gray-300 hover:border-primary/50 hover:text-primary"
            }`}
          >
            {vaultSectionOpen ? "Hide Vault" : "Browse Vault"}
          </button>
        </div>

        {importError && (
          <p className="text-xs text-red-500">{importError}</p>
        )}

        {vaultSectionOpen && (
          <div className="flex flex-col gap-2 p-3 border border-gray-200 rounded-md bg-gray-50">
            <p className="text-xs font-medium text-gray-600">Pronunciation Vaults</p>
            <p className="text-xs text-gray-500">Apply a curated collection to add all its pairs at once.</p>

            {vaultsLoading && (
              <p className="text-xs text-gray-400 py-4 text-center">Loading vaults...</p>
            )}

            {!vaultsLoading && vaults.length === 0 && (
              <p className="text-xs text-gray-500 py-4 text-center">No vaults available. Ask your admin to create some.</p>
            )}

            <div className="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1">
              {!vaultsLoading && vaults.map((vault) => (
              <div
                key={vault.id}
                className="flex flex-col gap-2 p-3 rounded-md border border-gray-200 bg-white hover:border-primary/40 hover:bg-primary/5 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900">{vault.name}</span>
                      {vault.accent && (
                        <Badge className="bg-primary/10 text-primary text-xs shrink-0">
                          {vault.accent}
                        </Badge>
                      )}
                      <span className="text-xs text-gray-400 shrink-0">
                        {vault.pairs.length} {vault.pairs.length === 1 ? "word" : "words"}
                      </span>
                    </div>
                    {vault.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{vault.description}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleApplyVault(vault)}
                    className="shrink-0 px-3 py-1.5 text-xs font-medium text-white rounded-md bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:brightness-105 transition-all"
                  >
                    Apply
                  </button>
                </div>

                {vault.pairs.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {vault.pairs.slice(0, 6).map((p, i) => (
                      <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        {p.word}
                      </span>
                    ))}
                    {vault.pairs.length > 6 && (
                      <span className="text-xs text-gray-400 px-1 py-0.5">
                        +{vault.pairs.length - 6} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {pronunciations.map((pronunciation, index) => (
            <div key={index} className="flex flex-col gap-1.5">
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Word</label>
                  <input
                    type="text"
                    value={pronunciation.word}
                    onChange={(e) => updatePronunciation(index, "word", e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 'LinkedIn'"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Pronunciation</label>
                  <input
                    type="text"
                    value={pronunciation.pronunciation}
                    onChange={(e) => updatePronunciation(index, "pronunciation", e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 'Linked In'"
                  />
                </div>
                <button
                  onClick={() => removePronunciation(index)}
                  className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md p-2 transition-colors border border-red-200"
                  title="Remove pronunciation"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <SuggestionBadge index={index} />
            </div>
          ))}
        </div>

        {pronunciations.length === 0 && !vaultSectionOpen && (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">No custom pronunciations added yet.</p>
            <p className="text-xs mt-1">Click "Add Pronunciation" to get started.</p>
          </div>
        )}

        {SuggesterPanel}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error?.message || "Failed to update pronunciations"}</p>
          </div>
        )}

        <div className="flex gap-3 pt-2 border-t">
          <button
            onClick={handleSave}
            disabled={isPending}
            className="px-6 py-2 text-white rounded-md transition-colors disabled:opacity-50 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:brightness-105"
          >
            {isPending
              ? "Saving..."
              : isSynced && pronunciations.length > 0
                ? "Update & Sync"
                : "Save & Sync with Genie"}
          </button>
          <button
            onClick={onClose}
            disabled={isPending}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </CoreDrawer>
  );
};

export default PronunciationModal;
